// What a job's input looks like (G1 decision 30): a library file, an earlier job's output or a
// team's AI Actor, read for its picture as each is read elsewhere (a file's thumb, an output's
// URL, an actor's portrait). The ad copy's inspiration and ⓘ's inputs both show one.
import { useGetAsset } from "../../api/generated/endpoints/assets/assets";
import { useGetJob, useGetTeamActor } from "../../api/generated/endpoints/generation/generation";
import type { ActorSource } from "../../api/generated/model";

export interface RefPicture {
  /** A still to show: a picture, a video's poster or thumb, an actor's portrait. */
  still: string | null;
  /** A video with no still: its file, to show a frame of. */
  video: string | null;
  kind: "image" | "video" | "audio" | null;
  /** The file's or the actor's name, when the read gives one. */
  name: string | null;
}

const NONE: RefPicture = { still: null, video: null, kind: null, name: null };

function kindOf(mime: string | null): RefPicture["kind"] {
  if (mime === null) return null;
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return mime.startsWith("audio/") ? "audio" : null;
}

/** The picture of a source, read when it is not null; nothing until read, or when it fails. */
export function useRefPicture(teamId: string, brandId: string, ref: ActorSource | null): RefPicture {
  const asset = useGetAsset(teamId, brandId, ref?.kind === "asset" ? ref.asset_id : "", {
    query: { enabled: ref?.kind === "asset" },
  }).data;
  const job = useGetJob(teamId, ref?.kind === "job_output" ? ref.job_id : "", {
    query: { enabled: ref?.kind === "job_output" },
  }).data;
  const actor = useGetTeamActor(teamId, ref?.kind === "actor" ? ref.actor_id : "", {
    query: { enabled: ref?.kind === "actor" },
  }).data;
  if (ref?.kind === "asset" && asset !== undefined) {
    const still = asset.kind === "image" ? (asset.urls.thumb ?? asset.urls.original) : (asset.urls.poster ?? asset.urls.thumb);
    return {
      still,
      video: asset.kind === "video" && still === null ? asset.urls.original : null,
      kind: asset.kind,
      name: asset.name,
    };
  }
  if (ref?.kind === "job_output" && job !== undefined) {
    const output = job.outputs.find((each) => each.index === ref.output_index);
    const kind = kindOf(output?.mime ?? null);
    const url = output?.url ?? null;
    return { still: kind === "image" ? url : null, video: kind === "video" ? url : null, kind, name: null };
  }
  if (ref?.kind === "actor" && actor !== undefined) {
    return { still: actor.image_url, video: null, kind: "image", name: actor.name };
  }
  return NONE;
}
